import React, { useEffect, useState } from 'react';

import type { UserDTO } from '../../services/apiClient';

import Table from '../../components/common/Table';
import { useUserApi } from '../../hooks/useUserApi';

interface UserTableProps {
  users: UserDTO[];
  onEdit: (user: UserDTO) => void;
  onDelete: (user: UserDTO) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  isLoading: boolean;
}

const getUserInitials = (user: UserDTO): string => {
  const initials =
    `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();

  return initials || '?';
};

const ProfileImage: React.FC<{ user: UserDTO }> = ({ user }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { getProfilePictureSas } = useUserApi();

  useEffect(() => {
    const fetchImage = async () => {
      if (user.profilePicture && user.id) {
        try {
          const response = await getProfilePictureSas(user.id);
          if (response?.success && response.data) {
            setImageUrl(response.data);
          }
        } catch (error) {
          console.error(`Error fetching image for user ${user.id}:`, error);
        }
      }
      setIsLoading(false);
    };

    fetchImage();
  }, [user.id, user.profilePicture]);

  if (isLoading) {
    return (
      <div
        className="rounded-full bg-gray-200 animate-pulse"
        style={{ width: '40px', height: '40px' }}
      />
    );
  }

  if (user.profilePicture && imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={`${user.firstName} ${user.lastName}`}
        className="rounded-full object-cover"
        style={{ width: '40px', height: '40px' }}
      />
    );
  }

  return (
    <div
      className="rounded-full bg-gray-300 text-white flex items-center justify-center"
      style={{ width: '40px', height: '40px' }}
    >
      {getUserInitials(user)}
    </div>
  );
};

const UserTable: React.FC<UserTableProps> = ({
  users,
  onEdit,
  onDelete,
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  isLoading,
}) => (
  <Table
    columns={[
      {
        label: '',
        key: 'profilePicture',
        isImage: true,
        imageSize: '40px',
        className: 'hidden sm:table-cell',
        render: (user) => <ProfileImage user={user} />,
      },
      { label: 'First Name', key: 'firstName' },
      { label: 'Last Name', key: 'lastName' },
      { label: 'Email', key: 'email', className: 'hidden sm:table-cell' },
    ]}
    data={users}
    actions={{
      onEdit: (user) => onEdit(user),
      onDelete: (user) => onDelete(user),
    }}
    currentPage={currentPage}
    totalPages={totalPages}
    onPageChange={onPageChange}
    pageSize={pageSize}
    onPageSizeChange={onPageSizeChange}
    isLoading={isLoading}
  />
);

export default UserTable;
